/**
 * Edge adjacency — what each side of a section or an opening meets.
 *
 * Trim is chosen by what an edge abuts: the wall's own perimeter, another
 * section (and which product and colour), an opening, or bare wall. This module
 * answers that for everything placed on a surface, as SEGMENTS: one side can
 * meet several things along its length (boards for the first six feet, then
 * bare wall), and each stretch needs its own trim and its own length.
 *
 * It only reports what is there. Which trim that calls for is a separate
 * decision (the trim rules), kept out of here on purpose.
 *
 * Things on a surface sit in LAYERS (see types/sections.ts):
 *   0  fills     — Wall&CeilingBoard
 *   1  inserts   — SlatWall, floating over board
 *   2  openings  — windows and doors, cut through everything
 *
 *   - Each edge meets whatever lies just beyond it — at the same distance, the
 *     thing on the higher layer, because that is what you would see there.
 *   - An edge can be hidden: where something on a higher layer straddles it,
 *     that stretch belongs to the higher thing ('covered') and there is nothing
 *     to trim.
 *   - Things don't have to touch exactly. Anything within `EDGE_REACH_FT`
 *     counts as meeting the edge — the 1" expansion gap left above a SlatWall
 *     band is still "boards above", not bare wall — and the gap is reported.
 *   - On a wall with a shaped top (`Surface.outline`, under a sloped ceiling)
 *     the edges are cut to the wall: a side stops where the wall does, and
 *     where the wall's top crosses a section the section's real top edge is
 *     the RAKE — reported as the perimeter, with its true sloped length and
 *     the heights at each end (`rake`).
 */

import { ProductColor } from '../../types';
import { EdgeSide, Opening, OpeningKind, Section, Surface, SectionProductType, sectionFillMode } from '../../types/sections';
import { Rect, rectOf } from './resolver';
import { Outline, outlineBreaks, outlineRakeLength, outlineSpansAbove, outlineTopAt, surfaceOutline } from './outline';

export type { EdgeSide };

export const EDGE_SIDES: EdgeSide[] = ['top', 'right', 'bottom', 'left'];

/** How far beyond an edge something can be and still count as meeting it (2"). */
export const EDGE_REACH_FT = 2 / 12;

export type EdgeNeighbour =
  /** The edge of the surface itself: `side` says which (a wall's bottom is the floor line). */
  | { kind: 'perimeter'; side: EdgeSide }
  /** Another section; `gap` is the distance to it in feet (0 when they touch). */
  | { kind: 'section'; sectionId: string; productType: SectionProductType; color: ProductColor; gap: number }
  /** A window or door. */
  | { kind: 'opening'; openingId: string; openingKind: OpeningKind; gap: number }
  /** Nothing — bare wall. */
  | { kind: 'bare' }
  /** This stretch of the edge is under something on a higher layer: there is no edge here to trim. */
  | { kind: 'covered'; by: string };

export interface EdgeSegment {
  /** Id of the section — or opening — whose edge this is. */
  sectionId: string;
  side: EdgeSide;
  /**
   * Where along the edge, in surface coordinates: x for the top / bottom sides,
   * y for the left / right sides. `from` < `to`.
   */
  from: number;
  to: number;
  /** The length of trim this stretch would take, in feet: `to - from`, or longer along a rake. */
  length: number;
  neighbour: EdgeNeighbour;
  /**
   * Set when this stretch runs along the sloped top of the wall rather than
   * the section's own level edge: the wall's height at `from` and at `to`.
   */
  rake?: [number, number];
}

const EPS = 1e-6;
const PROBE = 1e-4;

/** Anything placed on a surface, reduced to what adjacency needs. */
interface Placed {
  id: string;
  rect: Rect;
  layer: 0 | 1 | 2;
  section?: Section;
  opening?: Opening;
}

function placedOn(surface: Surface): Placed[] {
  return [
    ...surface.sections.map((section): Placed => ({
      id: section.id,
      rect: rectOf(section),
      layer: sectionFillMode(section.productType) === 'insert' ? 1 : 0,
      section,
    })),
    ...(surface.openings ?? []).map((opening): Placed => ({
      id: opening.id,
      rect: { x: opening.x, y: opening.y, width: opening.width, height: opening.height },
      layer: 2,
      opening,
    })),
  ];
}

/** Same neighbour? (Two stretches meeting the same thing are one segment.) */
function sameNeighbour(a: EdgeNeighbour, b: EdgeNeighbour): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'perimeter') return a.side === (b as typeof a).side;
  if (a.kind === 'section') return a.sectionId === (b as typeof a).sectionId && Math.abs(a.gap - (b as typeof a).gap) < EPS;
  if (a.kind === 'opening') return a.openingId === (b as typeof a).openingId && Math.abs(a.gap - (b as typeof a).gap) < EPS;
  if (a.kind === 'covered') return a.by === (b as typeof a).by;
  return true;
}

function segmentsFor(surface: Surface, id: string, side: EdgeSide): EdgeSegment[] {
  const all = placedOn(surface);
  const self = all.find((p) => p.id === id);
  if (!self) return [];
  const others = all.filter((p) => p.id !== id);
  const r = self.rect;
  const horiz = side === 'top' || side === 'bottom';

  // "along" runs with the edge, "across" is perpendicular to it; `out` is +1
  // when beyond the edge is toward higher coordinates.
  const along0 = horiz ? r.x : r.y;
  const along1 = along0 + (horiz ? r.width : r.height);
  const edge = side === 'top' ? r.y + r.height : side === 'bottom' ? r.y : side === 'right' ? r.x + r.width : r.x;
  const out = side === 'top' || side === 'right' ? 1 : -1;
  const surfaceEnd = out === 1 ? (horiz ? surface.height : surface.width) : 0;

  const alongOf = (o: Rect): [number, number] => (horiz ? [o.x, o.x + o.width] : [o.y, o.y + o.height]);
  const acrossOf = (o: Rect): [number, number] => (horiz ? [o.y, o.y + o.height] : [o.x, o.x + o.width]);

  // Everywhere along the edge that something else starts or stops.
  const stops = new Set<number>([along0, along1]);
  for (const o of others) {
    for (const v of alongOf(o.rect)) if (v > along0 + EPS && v < along1 - EPS) stops.add(v);
  }
  const marks = [...stops].sort((a, b) => a - b);

  const segments: EdgeSegment[] = [];
  for (let i = 0; i < marks.length - 1; i++) {
    const from = marks[i];
    const to = marks[i + 1];
    if (to - from < EPS) continue;
    const mid = (from + to) / 2;
    const spans = (o: Placed) => { const [a, b] = alongOf(o.rect); return a <= mid && mid <= b; };
    /** Does `o` cover the point `d` feet beyond the edge (negative = inside it)? */
    const coversAt = (o: Placed, d: number) => { const [c0, c1] = acrossOf(o.rect); const p = edge + out * d; return c0 <= p && p <= c1; };

    let neighbour: EdgeNeighbour | null = null;

    // An edge under something on a higher layer that straddles it is not an edge at all.
    const cover = others.find((o) => o.layer > self.layer && spans(o) && coversAt(o, -PROBE) && coversAt(o, PROBE));
    if (cover) neighbour = { kind: 'covered', by: cover.id };

    if (!neighbour) {
      // The nearest thing beyond the edge, within reach. Something lying right
      // across the edge (an inset's edge over the board it sits in) is at gap 0.
      let best: { placed: Placed; gap: number } | null = null;
      for (const o of others) {
        if (!spans(o)) continue;
        const [c0, c1] = acrossOf(o.rect);
        const near = out === 1 ? c0 : c1; // the neighbour's side facing this edge
        const far = out === 1 ? c1 : c0;
        if (out * (far - edge) <= PROBE) continue; // entirely on this side of the edge
        const gap = Math.max(0, out * (near - edge));
        if (gap > EDGE_REACH_FT + EPS) continue;
        // Nearest wins; at the same distance, the higher layer is the one in front.
        if (!best || gap < best.gap - EPS || (Math.abs(gap - best.gap) <= EPS && o.layer > best.placed.layer)) {
          best = { placed: o, gap };
        }
      }
      if (best?.placed.section) {
        const s = best.placed.section;
        neighbour = { kind: 'section', sectionId: s.id, productType: s.productType, color: s.color, gap: best.gap };
      } else if (best?.placed.opening) {
        const o = best.placed.opening;
        neighbour = { kind: 'opening', openingId: o.id, openingKind: o.kind, gap: best.gap };
      } else if (Math.abs(surfaceEnd - edge) <= EDGE_REACH_FT + EPS) {
        neighbour = { kind: 'perimeter', side };
      } else {
        neighbour = { kind: 'bare' };
      }
    }

    const last = segments[segments.length - 1];
    if (last && Math.abs(last.to - from) < EPS && sameNeighbour(last.neighbour, neighbour)) {
      last.to = to;
      last.length = last.to - last.from;
    } else {
      segments.push({ sectionId: id, side, from, to, length: to - from, neighbour });
    }
  }
  const outline = surfaceOutline(surface);
  return outline ? clipToOutline(outline, id, r, side, segments) : segments;
}

/** Keep the part of a level segment between `a` and `b`. */
function cut(seg: EdgeSegment, a: number, b: number): EdgeSegment | null {
  const from = Math.max(seg.from, a);
  const to = Math.min(seg.to, b);
  return to - from > EPS ? { ...seg, from, to, length: to - from } : null;
}

/**
 * Cut a rectangle's edge segments to the wall's shape. The left and right
 * sides stop where the wall's top is; the bottom (and a level top) exist only
 * where the wall rises above them; where the wall's top passes through the
 * rectangle, that stretch of its top edge is the rake.
 */
function clipToOutline(outline: Outline, id: string, r: Rect, side: EdgeSide, segments: EdgeSegment[]): EdgeSegment[] {
  const y1 = r.y + r.height;
  if (side === 'left' || side === 'right') {
    const top = outlineTopAt(outline, side === 'left' ? r.x : r.x + r.width);
    return segments.map((s) => cut(s, -Infinity, top)).filter((s): s is EdgeSegment => s !== null);
  }
  if (side === 'bottom') {
    return outlineSpansAbove(outline, r.y, r.x, r.x + r.width)
      .flatMap(([a, b]) => segments.map((s) => cut(s, a, b)))
      .filter((s): s is EdgeSegment => s !== null);
  }
  const marks = [r.x, ...outlineBreaks(outline, r.x, r.x + r.width, [r.y, y1]), r.x + r.width];
  const out: EdgeSegment[] = [];
  for (let i = 0; i < marks.length - 1; i++) {
    const a = marks[i];
    const b = marks[i + 1];
    if (b - a < EPS) continue;
    const top = outlineTopAt(outline, (a + b) / 2);
    if (top <= r.y + EPS) continue; // no wall here at all
    if (top < y1 - EPS) {
      // The wall's top runs through the rectangle: the rake is the real top edge.
      out.push({
        sectionId: id, side, from: a, to: b, length: outlineRakeLength(outline, a, b),
        neighbour: { kind: 'perimeter', side }, rake: [outlineTopAt(outline, a), outlineTopAt(outline, b)],
      });
      continue;
    }
    for (const s of segments) {
      const c = cut(s, a, b);
      if (!c) continue;
      // A level stretch continuing the previous one (split only by a bend in the wall's top) is one segment.
      const last = out[out.length - 1];
      if (last && !last.rake && Math.abs(last.to - c.from) < EPS && sameNeighbour(last.neighbour, c.neighbour)) {
        last.to = c.to;
        last.length = last.to - last.from;
      } else out.push(c);
    }
  }
  return out;
}

/** What one side of `section` meets, in order along the edge. */
export function edgeSegments(surface: Surface, section: Section, side: EdgeSide): EdgeSegment[] {
  return segmentsFor(surface, section.id, side);
}

/** What one side of an opening meets, in order along the edge. */
export function openingEdgeSegments(surface: Surface, opening: Opening, side: EdgeSide): EdgeSegment[] {
  return segmentsFor(surface, opening.id, side);
}

/** All four sides of one section. */
export function sectionEdges(surface: Surface, section: Section): EdgeSegment[] {
  return EDGE_SIDES.flatMap((side) => edgeSegments(surface, section, side));
}

/** Every edge segment of every section on a surface. */
export function surfaceEdges(surface: Surface): EdgeSegment[] {
  return surface.sections.flatMap((section) => sectionEdges(surface, section));
}
